///
/// EarthBrowser - Webcam Browser
///
/// Movable window for browsing the global webcam directory using local sync data
///
/// Created by Matt Giger on 9/4/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DataBrowserView, type DataBrowserViewHandle } from '@/components/data-browser/DataBrowserView'
import { Switch } from '@/components/ui/switch'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { DatasetVisibilityToggle } from '@/components/windows/DatasetVisibilityToggle'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { WebcamDataProvider } from '@/data-providers/WebcamDataProvider'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import type { DataRow } from '@/data-providers/types'
import { sendDatasetAction } from '@/redux/earthBrowserSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { WebcamStationsTable } from '@/earthbrowser/dataset-distribution/webcams/WebcamStationsTable'
import { useDatasetLayerRefresh } from '@/hooks/useDatasetLayerRefresh'
import { pushTargetToHistory } from '@/utils/navigationHistory'
import type { WebcamEntry } from '@shared-types/webcam'
import { Play, Video } from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from '@/earthbrowser/utils/platform'
import { closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'

interface WebcamBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const WebcamBrowserRenderer = memo(function WebcamBrowserRenderer({
	windowId,
	windowState,
}: WebcamBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const webcamsVisible = visibleDatasets.includes("webcam")

	const layerManager = useAppSelector(state => state.datasetScene.layerManager)

	const [category, setCategory] = useState<string | undefined>(undefined)
	const [liveOnly, setLiveOnly] = useState<boolean>(false)
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [webcams, setWebcams] = useState<WebcamEntry[]>([])
	const [categories, setCategories] = useState<string[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [refreshTrigger, setRefreshTrigger] = useState(0)
	const dataBrowserViewRef = useRef<DataBrowserViewHandle>(null)

	// Kept in a ref so the provider's queryFn always reads the current rows
	const entriesRef = useRef<WebcamEntry[]>([])

	const loadWebcams = useCallback(async (showLoading: boolean) => {
		if (showLoading) setIsLoading(true)
		try {
			const all = await WebcamStationsTable.getAll()
			setTotalCount(all.length)
			setCategories([...new Set(all.map(w => w.category))].sort())

			let filtered = all
			if (category) {
				filtered = filtered.filter(w => w.category === category)
			}
			if (liveOnly) {
				filtered = filtered.filter(w => w.stream_url)
			}

			entriesRef.current = filtered
			setWebcams(filtered)
			setRefreshTrigger(prev => prev + 1)
		} catch (error) {
			console.error('Failed to load webcams:', error)
		} finally {
			if (showLoading) setIsLoading(false)
		}
	}, [category, liveOnly])

	useEffect(() => {
		void loadWebcams(true)
	}, [loadWebcams])

	// Reload silently whenever the webcams layer refreshes after a sync,
	// so the table doesn't show a stale snapshot from when the window opened
	useDatasetLayerRefresh('webcams', () => {
		void loadWebcams(false)
	})

	// Initialize and update data provider
	useEffect(() => {
		if (isLoading) return

		if (!isInitialized) {
			const oldProvider = DataProviderRegistry.get(windowId)
			if (oldProvider) {
				void oldProvider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}

			const provider = new WebcamDataProvider({
				id: windowId,
				name: 'Webcams',
				queryFn: async () => entriesRef.current
			})

			void provider.initialize().then(() => {
				DataProviderRegistry.register(windowId, provider)
				setIsInitialized(true)
			})
		} else {
			const provider = DataProviderRegistry.get(windowId) as WebcamDataProvider
			if (provider) {
				void provider.refreshData()
			}
		}
	}, [webcams, windowId, isLoading, isInitialized])

	// Sync scene layer visibility with Redux state
	useEffect(() => {
		if (layerManager) {
			layerManager.setLayerVisible('webcams', webcamsVisible)
		}
	}, [layerManager, webcamsVisible])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const provider = DataProviderRegistry.get(windowId)
			if (provider) {
				void provider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}
		}
	}, [windowId])

	// Handle webcam row click: show the layer, fly to and focus the camera
	const handleRowClick = useCallback((row: DataRow) => {
		const webcam = row as unknown as WebcamEntry
		const webcamId = String(webcam.id)

		if (!webcamsVisible) {
			dispatch(sendDatasetAction({
				type: "webcam",
				action: "enable"
			}))
			if (layerManager) {
				layerManager.setLayerVisible('webcams', true)
			}
		}

		if (layerManager) {
			layerManager.focusSceneObject('webcams', webcamId)
		}

		pushTargetToHistory('webcam', webcamId)

		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [layerManager, webcamsVisible, dispatch, windowId])

	const handleRefresh = useCallback(() => {
		void loadWebcams(true)
	}, [loadWebcams])

	// Header actions (category filter, live-stream toggle)
	const headerActions = useMemo(() => (
		<div className="flex items-center gap-2 p-1">
			{categories.length > 0 && (
				<div className="flex items-center gap-1 bg-black/30 rounded-full p-1 pr-2 border border-white/10">
					<select
						value={category || 'All'}
						onChange={(e) => setCategory(e.target.value === 'All' ? undefined : e.target.value)}
						className="px-3 py-1 text-xs rounded-full bg-transparent text-white/80 border-0 outline-none cursor-pointer hover:text-white capitalize"
					>
						<option value="All">All Categories</option>
						{categories.map((c) => (
							<option key={c} value={c} className="capitalize">{c}</option>
						))}
					</select>
				</div>
			)}

			<div className="flex items-center gap-1.5 bg-black/30 rounded-full px-3 py-1.5 border border-white/10">
				<Switch
					checked={liveOnly}
					onCheckedChange={setLiveOnly}
					className="data-[state=checked]:bg-fuchsia-600 scale-75"
				/>
				<Play className="w-3 h-3 text-fuchsia-400" />
				<span className="text-xs text-white/80">Streams</span>
			</div>
		</div>
	), [categories, category, liveOnly])

	// Content
	const content = useMemo(() => {
		const provider = DataProviderRegistry.get(windowId)

		if (provider && isInitialized && !isLoading) {
			return (
				<DataBrowserView
					ref={dataBrowserViewRef}
					key={`webcam-browser-${refreshTrigger}`}
					provider={provider}
					onRowClick={handleRowClick}
					onRefresh={handleRefresh}
					totalDatabaseCount={totalCount}
					searchPlaceholder="Search webcams by name, region, or source..."
				/>
			)
		}

		return (
			<div className="flex flex-col items-center justify-center h-full bg-black/20 backdrop-blur-sm">
				<div className="flex flex-col items-center gap-4 p-8">
					<div className="relative w-16 h-16">
						<div className="absolute inset-0 border-4 border-fuchsia-500/20 rounded-full" />
						<div className="absolute inset-0 border-4 border-transparent border-t-fuchsia-500 rounded-full animate-spin" />
					</div>
					<p className="text-base font-medium text-white">
						Loading Webcams
					</p>
					<p className="text-sm text-white/60">
						{isLoading ? 'Fetching data...' : 'Initializing...'}
					</p>
				</div>
			</div>
		)
	}, [windowId, isInitialized, isLoading, handleRowClick, handleRefresh, totalCount, refreshTrigger])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	const handleBringToFront = useCallback(() => {
		dataBrowserViewRef.current?.focusSearch()
	}, [])

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<Video className="w-5 h-5" />}
			iconColor="text-fuchsia-400"
			headerActions={headerActions}
			titlebarPrefix={<DatasetVisibilityToggle windowType="webcam" />}
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})
